import { SidePanelClose, SidePanelOpen } from "@carbon/icons-react/lib";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { type FC, useState } from "react";

import {
	ResizeBorder,
	type ResizeOffset,
	XResizeDisplayComponent,
} from "@moment/design-system/ResizeBorder";

import { useResponsive } from "~/components/canvas/hooks/useResponsive";
import { HotKeyTooltip } from "~/components/common/HotKeyToolTip";
import { useScopedKeyBinding } from "~/components/common/keybindings";
import { IconButton } from "~/ui/Button";
import { hotkeys } from "~/utils/hotkeys";

import { useEasel } from "../easel";
import { Announcement } from "./Announcement";
import { useNavigationPane } from "./NavigationPaneContext";
import { NewPageButton } from "./NewPageButton";
import { PageList } from "./PageList";

const MIN_WIDTH = 250;
const DURATION = 0.1;

export const NavigationPane: FC = () => {
	const { isExpanded, toggleExpanded } = useNavigationPane();
	const { innerWidth, isSm } = useResponsive();
	const [width, setWidth] = useState<number>(MIN_WIDTH);
	const [dragStartWidth, setDragStartWidth] = useState<number>(MIN_WIDTH);
	const [duration, setDuration] = useState(DURATION);
	const { canvasWritable } = useEasel();

	const hotkey = hotkeys["toggle-navigation-pane"];
	useScopedKeyBinding(hotkey.mousetrapKeybinding(), toggleExpanded);
	const smWidth = innerWidth - 48;
	const openStyle = {
		minWidth: isSm ? MIN_WIDTH : smWidth,
		width: isSm ? width : smWidth,
	};

	const handleResizeMove = (offset: ResizeOffset) => {
		setDuration(0);
		setWidth(dragStartWidth - offset.x);
	};

	const handleResizeStart = () => {
		setDragStartWidth(width < MIN_WIDTH ? MIN_WIDTH : width);
	};

	const handleResizeEnd = () => {
		setDuration(DURATION);
	};

	return (
		<>
			<div className="flex">
				<AnimatePresence>
					{isExpanded && (
						<>
							<motion.div
								className={cx(
									"bg-secondary flex flex-col border-r",
									{ "z-popover fixed bottom-0 left-0 top-0": !isSm },
									// don't print navigation pane
									"print:hidden"
								)}
								style={openStyle}
								initial="open"
								animate="open"
								exit="collapsed"
								variants={{
									open: {
										x: "0",
										...openStyle,
									},
									collapsed: { x: "-100%", width: 0 },
								}}
								transition={{ duration }}
							>
								<div className="flex-1 overflow-y-auto my-2">
									<div className="mb-4">
										<PageList />
										{canvasWritable && <NewPageButton />}
									</div>
								</div>
								<Announcement />
							</motion.div>
							{isSm && (
								<ResizeBorder
									onResizeMove={handleResizeMove}
									onResizeStart={handleResizeStart}
									onResizeEnd={handleResizeEnd}
									borderDisplayComponent={XResizeDisplayComponent}
								/>
							)}
						</>
					)}
				</AnimatePresence>
			</div>
			<div className="relative">
				<AnimatePresence>
					<motion.div
						className={cx("flex flex-col pl-2 pt-2  print:hidden", {
							"z-app-toggles fixed bottom-0 top-0": !isSm,
							"bg-primary z-app-toggles-expanded right-0 pr-2": !isSm && isExpanded,
							"z-app-toggles absolute": isSm,
						})}
						initial="collapsed"
						animate="open"
						exit="collapsed"
						variants={{
							open: { opacity: 1 },
							collapsed: { opacity: 0, transition: { duration: 0 } },
						}}
						transition={{ duration: DURATION, delay: DURATION }}
					>
						<HotKeyTooltip
							hotkeyName="toggle-navigation-pane"
							contentProps={{ side: "right" }}
						>
							<IconButton
								preset="quaternary"
								onClick={toggleExpanded}
								toggled={isExpanded}
								icon={isExpanded ? <SidePanelClose /> : <SidePanelOpen />}
								label="Toggle navigation pane"
								data-testid="toggle-navigation-pane"
							/>
						</HotKeyTooltip>
					</motion.div>
				</AnimatePresence>
			</div>
		</>
	);
};
